import { Box, Link, Typography } from '@mui/material';
import { apiUrls } from '@/constant/configure';
import { HEADLINE_WORDS } from './authHeadlineWords';
import soduBrandMark from '@/assets/sodu/brand-mark.svg';
import soduWordmark from '@/assets/sodu/wordmark.svg';

/**
 * 로그인 화면 좌측 브랜드 패널 — 데스크톱 전용.
 *
 * 글로릴스와 같은 구조(다크 배면 + 큰 글로우 1개 + 상단 로고 / 중앙 워드 스택 / 하단 카피)를
 * 소듀 핑크(primary)로 옮긴 것. 색만 소듀, 골격은 글로릴스.
 */
export interface AuthBrand {
    mark: string;
    /** 워드마크 이미지. 없으면 label 텍스트를 쓴다. */
    wordmark?: string;
    label: string;
}

/**
 * 워드마크 자산은 전부 다크 텍스트(#1a1a1a)다.
 * `wordmark-white.svg` 는 "흰 글자"가 아니라 "흰 배경" 변형이라 다크 패널에서 흰 박스로 보인다.
 * 그래서 배경이 없는 `wordmark.svg` 를 가져와 필터로 흰색화한다 — 손글씨 레터링 형태는 그대로 남는다.
 */
const SODU_BRAND: AuthBrand = { mark: soduBrandMark, wordmark: soduWordmark, label: '소셜 듀오' };

export default function AuthBrandPanel({ brand = SODU_BRAND }: { brand?: AuthBrand }) {
    return (
        <Box
            aria-hidden
            sx={{
                display: { xs: 'none', lg: 'flex' },
                position: 'relative',
                overflow: 'hidden',
                flexDirection: 'column',
                justifyContent: 'space-between',
                p: 6,
                bgcolor: '#141414',
                color: '#fafafa',
            }}
        >
            {/* primary 글로우 — 핑크 기운만 은은하게 */}
            <Box
                sx={{
                    pointerEvents: 'none',
                    position: 'absolute',
                    left: -160,
                    top: '33%',
                    width: 544,
                    height: 544,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    opacity: 0.25,
                    filter: 'blur(120px)',
                }}
            />

            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Link href={`${apiUrls.spacehome_url}/influencer`} target="_blank" sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                    <Box component="img" src={brand.mark} alt="" sx={{ width: 34, height: 34, objectFit: 'contain' }} />
                    {brand.wordmark ? (
                        <Box
                            component="img"
                            src={brand.wordmark}
                            alt={brand.label}
                            sx={{
                                height: 26,
                                width: 'auto',
                                objectFit: 'contain',
                                filter: 'brightness(0) invert(1)',
                            }}
                        />
                    ) : (
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#fafafa' }}>
                            {brand.label}
                        </Typography>
                    )}
                </Link>
            </Box>

            {/* 중앙 블록 — 헤드라인 + 워드 스택을 한 덩어리로 (워드 라인이 바닥까지 처지지 않게) */}
            <Box sx={{ position: 'relative', maxWidth: 560 }}>
                {/* 전역 스케일이 h2 를 1.7rem 으로 눌러놔서(styles/index.scss) 크기를 직접 지정한다 */}
                <Typography
                    variant="h2"
                    sx={{ fontWeight: 700, lineHeight: 1.3, color: 'primary.main', fontSize: 'clamp(28px, 2.2vw, 36px)' }}
                >
                    글로벌 공구가 가능한
                </Typography>
                <Typography
                    variant="h2"
                    sx={{ fontWeight: 700, lineHeight: 1.3, color: '#fafafa', fontSize: 'clamp(28px, 2.2vw, 36px)' }}
                >
                    프로필 링크를 만들어 보세요
                </Typography>
                <Typography
                    variant="body1"
                    sx={{ mt: 2.5, color: 'rgba(250,250,250,0.62)', lineHeight: 1.8, fontSize: '1.05rem' }}
                >
                    하나의 링크로 전 세계에서 공동구매를 한 번에.
                </Typography>

                {/* 기능 워드 스택 — 흰/핑크/회색 리듬 */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        columnGap: 2,
                        rowGap: 0.5,
                        mt: 5,
                    }}
                >
                    {HEADLINE_WORDS.map((w, i) => (
                        <Typography
                            key={w}
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color:
                                    i % 3 === 0
                                        ? '#fafafa'
                                        : i % 3 === 1
                                          ? 'primary.main'
                                          : 'rgba(250,250,250,0.38)',
                            }}
                        >
                            {w}
                        </Typography>
                    ))}
                </Box>
            </Box>

            {/* space-between 유지용 스페이서 — 중앙 블록이 세로 중앙에 오게 한다 */}
            <Box aria-hidden />
        </Box>
    );
}
